import {
  SlideConfig,
  createEqualSections,
  createCustomSections,
  CHART_CONSTANTS,
} from "./_scoresChartHelpers";

const { LABEL_WIDTH, CHART_WIDTH } = CHART_CONSTANTS;
const CHART_START_X = LABEL_WIDTH;

// Hogan Personality Inventory Configuration
export const HPI_CONFIG: SlideConfig = {
  title: "Hogan Personality Inventory",
  dataKey: "hogan_personality_inventory",
  scaleColor: "#FCB326",
  scaleMaxValue: 100,
  descriptionText:
    "The HPI measures qualities that describe how leaders behave relative to others when they are at their best. Assessing overall personality provides valuable insight into how people work and lead, and how successful they will be. Percentile scores fall into Low, Average, and High ranges.",
  scaleDefinitions: [
    {
      title: "Adjustment",
      description:
        "Concerns resilience and the degree to which a person is calm and even tempered or, conversely, moody and easily upset",
    },
    {
      title: "Ambition",
      description: "Concerns competitive drive, energy, and goal-orientation",
    },
    {
      title: "Sociability",
      description: "Concerns social style and being socially proactive",
    },
    {
      title: "Interpersonal Sensitivity",
      description: "Concerns communication style, social skill, and tact",
    },
    {
      title: "Prudence",
      description: "Concerns task-orientation and conscientiousness",
    },
    {
      title: "Inquisitive",
      description:
        "Concerns idea-orientation, level of curiosity and openness to new ideas",
    },
    {
      title: "Learning Approach",
      description:
        "Concerns learning style and the degree to which a person prefers academic or formal learning versus hands-on training",
    },
  ],
  chartConfig: {
    barHeight: 0.3,
    barGap: 0.28,
    verticalLinePositions: [33, 67],
    sections: createEqualSections(
      ["LOW", "AVERAGE", "HIGH"],
      CHART_START_X,
      CHART_WIDTH
    ),
  },
};

// Hogan Development Survey Configuration
export const HDS_CONFIG: SlideConfig = {
  title: "Hogan Development Survey",
  dataKey: "hogan_development_survey",
  scaleColor: "#E11630",
  scaleMaxValue: 100,
  descriptionText:
    "The HDS measures the common \"derailers\" that emerge when an individual is under pressure or stress. These risk factors may impede effectiveness over time. Percentile scores fall into No-, Low-, Moderate-, and High-risk ranges. (Note that there can be a shadow to falling into a No-risk range.)",
  scaleDefinitions: [
    {
      title: "Excitable",
      description:
        "Concerns the degree to which one moves from excitement to disappointment",
    },
    {
      title: "Skeptical",
      description: "Concerns how one responds to social insight and feedback",
    },
    {
      title: "Cautious",
      description: "Concerns how careful and thorough one is relative to risk",
    },
    {
      title: "Reserved",
      description:
        "Concerns the degree to which one acts with independence and objectivity",
    },
    {
      title: "Leisurely",
      description: "Concerns cooperativeness and agreeability",
    },
    {
      title: "Bold",
      description:
        "Concerns the degree to which one projects confidence and assertiveness",
    },
    {
      title: "Mischievous",
      description:
        "Concerns being charming, risk-taking, and excitement-seeking",
    },
    {
      title: "Colorful",
      description: "Concerns being outgoing and socially skilled",
    },
    {
      title: "Imaginative",
      description: "Concerns being innovative and creative",
    },
    {
      title: "Diligent",
      description: "Concerns being supportive and loyal",
    },
    {
      title: "Dutiful",
      description:
        "Concerns being eager to please and reluctant to act independently",
    },
  ],
  chartConfig: {
    barHeight: 0.26,
    barGap: 0.26,
    verticalLinePositions: [40, 70, 90],
    sections: createCustomSections(
      [
        { label: "LITTLE TO NO RISK", widthPercent: 40 },
        { label: "LOW RISK", widthPercent: 30 },
        { label: "MODERATE RISK", widthPercent: 20 },
        { label: "HIGH RISK", widthPercent: 10 },
      ],
      CHART_START_X,
      CHART_WIDTH
    ),
  },
};

// Hogan Motives, Values, Preferences Inventory Configuration
export const MVPI_CONFIG: SlideConfig = {
  title: "Hogan Motives, Values, & Preferences Inventory",
  dataKey: "hogan_motives_values_preferences_inventory",
  scaleColor: "#5D89B3",
  scaleMaxValue: 100,
  descriptionText:
    "The MVPI measures leaders' goals, values, drivers, and interests that determine what we strive to attain. Together, these describe what a leader stands for and the environments in which they will flourish. Percentile scores fall into Low, Average, and High ranges.",
  scaleDefinitions: [
    {
      title: "Recognition",
      description: "Concerns the desire for appreciation and acknowledgement",
    },
    {
      title: "Power",
      description:
        "Concerns the drive to achieve results, control resources, and gain responsibility",
    },
    {
      title: "Hedonism",
      description:
        "Concerns wanting fun, variety, and enjoyment in the workplace",
    },
    {
      title: "Altruistic",
      description:
        "Concerns wanting to help people who are struggling or in need",
    },
    {
      title: "Affiliation",
      description:
        "Concerns seeking opportunities to build social networks and collaborate with people",
    },
    {
      title: "Tradition",
      description: "Concerns respecting structure, rules, and authority",
    },
    {
      title: "Security",
      description: "Concerns wanting stability, order, and predictability",
    },
    {
      title: "Commerce",
      description: "Concerns seeking wealth and financial success",
    },
    {
      title: "Aesthetics",
      description:
        "Concerns creating work products with a focus on innovation, style, or quality",
    },
    {
      title: "Science",
      description:
        "Concerns a preference for a rational and objective approach to decision-making",
    },
  ],
  chartConfig: {
    barHeight: 0.3,
    barGap: 0.28,
    verticalLinePositions: [33, 67],
    sections: createEqualSections(
      ["LOW", "AVERAGE", "HIGH"],
      CHART_START_X,
      CHART_WIDTH
    ),
  },
};

// Occupational Personality Questionnaire Configuration
export const OPQ_CONFIG: SlideConfig = {
  title: "Occupational Personality Questionnaire",
  dataKey: "opq",
  scaleColor: "#A590D8",
  scaleMaxValue: 10,
  descriptionText:
    "The OPQ measures personality from an occupational model. This model breaks personality down into three domains: Relationships with People, Thinking Style, and Feelings and Emotions. Scores are normally distributed on a Sten score scale from 1 to 10.",
  scaleDefinitions: [
    {
      title: "Innovative",
      description:
        "Generates new ideas, enjoys being creative, thinks of original solutions",
    },
    {
      title: "Optimistic",
      description:
        "Expects things will turn out well, looks to the positive aspects of a situation, has an optimistic view of the future",
    },
    {
      title: "Evaluative",
      description:
        "Critically evaluates information, looks for potential limitations, focuses upon errors",
    },
    {
      title: "Socially Confident",
      description:
        "Feels comfortable when first meeting people, at ease informal situations",
    },
    {
      title: "Forward Thinking",
      description:
        "Takes a long-term view, sets goals for the future, more likely to take a strategic perspective",
    },
    {
      title: "Affiliative",
      description:
        "Enjoys others’ company, likes to be around people, can miss the company of others",
    },
    {
      title: "Conventional",
      description:
        "Prefers well established methods, prefers a more conventional approach",
    },
    {
      title: "Rule Following",
      description:
        "Follows rules and regulations, prefers clear guidelines, finds it difficult to break rules",
    },
  ],
  chartConfig: {
    barHeight: 0.3,
    barGap: 0.28,
    verticalLinePositions: [30, 70],
    sections: createCustomSections(
      [
        { label: "16%", widthPercent: 30 },
        { label: "68%", widthPercent: 40 },
        { label: "16%", widthPercent: 30 },
      ],
      CHART_START_X,
      CHART_WIDTH
    ),
  },
};
